// src/lib/wiki-image.js
// Phase 37 — extracted from src/App.jsx (lines 2811–2967 originally; current
// ~2351–2505 in the post-storage/format/plants/location/products/ai/engagement
// state).
//
// Scope: Wikipedia/Commons image fetch chain + localStorage cache + operator
//        image overrides. Single source of truth for every plant image lookup.
// Public exports: IMGURL_CACHE_MAX, IMGURL_STORE, _storeImgUrl, fetchWikiImage,
//                 getImageOverrides, isPlantPage, setImageOverrides.
// Module state: lazy-initialised _imgCache, mutable _imageOverrides, _plantPageCache Map.
//
// Risk 10 (RESEARCH §H): the imgCache initialiser previously read localStorage
//   eagerly at module load. Wrapping it behind a lazy getter (`getImgCache()`)
//   means the first read happens only when a consumer actually needs it — so
//   load-order across the new feature folders cannot leave the cache mid-init.
//
// Risk 1 (RESEARCH §H): _imageOverrides + _plantPageCache are module-level
//   singletons. Vite ESM ensures one execution per module URL — every importer
//   shares the same Map. Test below asserts the round-trip.

import { WIKI_ALIASES } from '../data/wiki-aliases.js';
import { safeSetItem } from './storage.js';

export const IMGURL_STORE = 'tend:imgurls';
export const IMGURL_CACHE_MAX = 500; // trim to this many entries on each write

// === Module state — lazy getter for imgCache (Risk 10) ===
let _imgCache = null;
function getImgCache() {
  if (_imgCache !== null) return _imgCache;
  try {
    _imgCache = JSON.parse(localStorage.getItem(IMGURL_STORE) || '{}');
  } catch {
    _imgCache = {};
  }
  return _imgCache;
}

export function _storeImgUrl(key, url) {
  const cache = getImgCache();
  cache[key] = url;
  try {
    const s = JSON.parse(localStorage.getItem(IMGURL_STORE) || '{}');
    s[key] = url;
    // Trim oldest entries if over the cap
    const keys = Object.keys(s);
    if (keys.length > IMGURL_CACHE_MAX) {
      const overBy = keys.length - IMGURL_CACHE_MAX;
      keys.slice(0, overBy).forEach(k => delete s[k]);
    }
    safeSetItem(IMGURL_STORE, JSON.stringify(s));
  } catch {}
}

// Phase 34 IMG-01 / D-06 — operator-managed override map. Module-level mutable
// reference so all fetchWikiImage call sites pick up the live value the App
// component sets via setImageOverrides() in its on-load fetch effect.
// Shape: { "<wiki|latin>": { imageUrl?: string, hideImage?: true } }
let _imageOverrides = {};
export function setImageOverrides(map) {
  _imageOverrides = (map && typeof map === 'object' && !Array.isArray(map)) ? map : {};
}
export function getImageOverrides() { return _imageOverrides; }

// Phase 34 IMG-01 / D-05 — taxobox/category guard. Reject Wikipedia pages that
// don't look like plants (closes Rosa 'Juliet' → Julieta Montaño bug class).
// Single round-trip checks pageprops (taxobox/speciesbox marker) and a handful
// of plant-y categories. Cached per-title so multiple phases don't re-probe.
const _plantPageCache = new Map();
export async function isPlantPage(title) {
  if (!title) return false;
  if (_plantPageCache.has(title)) return _plantPageCache.get(title);
  let result = false;
  try {
    const url = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(title)}&prop=pageprops|categories&clshow=!hidden&cllimit=20&format=json&origin=*`;
    const r = await fetch(url);
    const d = await r.json();
    const page = Object.values(d?.query?.pages || {})[0];
    if (page) {
      // Marker A: taxobox-flavoured pageprops (most reliable — set by
      // {{Taxobox}}, {{Speciesbox}}, {{Automatic taxobox}}, {{Infobox plant}}).
      const props = page.pageprops || {};
      const propString = JSON.stringify(props).toLowerCase();
      if (propString.includes('taxobox') || propString.includes('speciesbox') || propString.includes('infobox plant')) {
        result = true;
      } else {
        // Marker B: any category title matches plant-y patterns.
        const cats = page.categories || [];
        for (const c of cats) {
          if (/Category:(Flora|Plants|Garden_plants|.*genera|.*species|Cultivars)/i.test(c.title || '')) {
            result = true;
            break;
          }
        }
      }
    }
  } catch {
    // Network failure — be permissive (return true) so we don't break the
    // existing happy path during a Wikipedia outage.
    result = true;
  }
  _plantPageCache.set(title, result);
  return result;
}

export async function fetchWikiImage(wiki, latin) {
  // Phase 0 (D-06): Operator-managed overrides win over every Wikipedia path.
  // Check both the wiki title AND the latin name as keys so triage edits land
  // for users without rebuilding the app.
  {
    const o = _imageOverrides[wiki] || _imageOverrides[latin];
    if (o) {
      const cacheKey = latin || wiki;
      if (o.hideImage === true) { _storeImgUrl(cacheKey, null); return null; }
      if (typeof o.imageUrl === 'string' && o.imageUrl) { _storeImgUrl(cacheKey, o.imageUrl); return o.imageUrl; }
    }
  }

  // Use latin as the cache key so each cultivar has its own entry
  const key = latin || wiki;
  const imgCache = getImgCache();
  if (imgCache[key] !== undefined) return imgCache[key];
  const genus = latin?.split(/\s+/)[0] || null;
  // Strip cultivar quotes: "Tulipa 'Black Parrot'" → "Tulipa Black Parrot"
  const latinClean = latin?.includes("'") ? latin.replace(/'/g, "").replace(/\s+/g, " ").trim() : null;
  // Keep cultivar-specific and generic lookups separate so genus image doesn't
  // shadow a missing cultivar result before we've tried Commons
  const specific = [...new Set([latin, latinClean].filter(Boolean))];
  const generic  = [...new Set([wiki, genus].filter(Boolean))];

  // Words from the genus+species that must appear in a Commons result title to avoid
  // false matches on common English phrases like "First Lady" or "Diana"
  const plantWords = (latin || wiki || "").toLowerCase().split(/\s+/).filter(w => w.length > 3 && !/^'/.test(w));

  const wikiPageImg = async t => { try { const d = await (await fetch(`https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(t)}&prop=pageimages&format=json&pithumbsize=400&origin=*`)).json(); return Object.values(d.query.pages)[0]?.thumbnail?.source || null; } catch { return null; } };
  const wikiSummary = async t => { try { const d = await (await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(t.replace(/ /g, "_"))}`)).json(); return d?.thumbnail?.source || null; } catch { return null; } };
  const commons = async t => {
    try {
      const s = await (await fetch(`https://commons.wikimedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(t)}&srnamespace=6&srlimit=5&format=json&origin=*`)).json();
      const results = s?.query?.search || [];
      // Reject SVGs (illustrations/diagrams) and results whose title shares no plant word
      const match = results.find(r => {
        const tl = r.title.toLowerCase();
        return !tl.endsWith(".svg") && plantWords.some(w => tl.includes(w));
      });
      if (!match) return null;
      const i = await (await fetch(`https://commons.wikimedia.org/w/api.php?action=query&titles=${encodeURIComponent(match.title)}&prop=imageinfo&iiprop=url&iiurlwidth=400&format=json&origin=*`)).json();
      return Object.values(i.query.pages)[0]?.imageinfo?.[0]?.thumburl || null;
    } catch { return null; }
  };

  // Phase 1 (D-05): cultivar-specific — Wikipedia pageimages then summary,
  // gated on isPlantPage() so non-plant articles never produce a thumbnail.
  for (const t of specific) {
    if (!await isPlantPage(t)) continue;
    const s = await wikiPageImg(t); if (s) { _storeImgUrl(key, s); return s; }
  }
  for (const t of specific) {
    if (!await isPlantPage(t)) continue;
    const s = await wikiSummary(t); if (s) { _storeImgUrl(key, s); return s; }
  }

  // Phase 2 (D-05): genus/wiki fallback with same isPlantPage gate.
  for (const t of generic) {
    if (!await isPlantPage(t)) continue;
    const s = await wikiPageImg(t); if (s) { _storeImgUrl(key, s); return s; }
  }
  for (const t of generic) {
    if (!await isPlantPage(t)) continue;
    const s = await wikiSummary(t); if (s) { _storeImgUrl(key, s); return s; }
  }

  // Phase 3 (D-05): alias map check, gated on isPlantPage(aliasTarget).
  const aliasKey = (wiki || "").toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
  const aliasTarget = WIKI_ALIASES[aliasKey];
  if (aliasTarget && await isPlantPage(aliasTarget)) {
    const s = await wikiPageImg(aliasTarget); if (s) { _storeImgUrl(key, s); return s; }
    const s2 = await wikiSummary(aliasTarget); if (s2) { _storeImgUrl(key, s2); return s2; }
  }

  // Phase 4 (D-05): REMOVED. The previous opensearch fuzzy fallback was the
  // proven false-match source for Rosa 'Juliet' → Julieta Montaño (issue #219).

  // Phase 5: Commons search — final fallback. Existing plantWords filter
  // serves the same role as isPlantPage; Commons file results don't have a
  // Wikipedia article title to gate on.
  for (const t of specific) { const s = await commons(t); if (s) { _storeImgUrl(key, s); return s; } }
  for (const t of generic)  { const s = await commons(t); if (s) { _storeImgUrl(key, s); return s; } }

  // Don't cache null — allows a retry if the plant's wiki/latin is later corrected
  return null;
}
